// ✅ 모바일 전용 SCARView - 웹사이트 코드에 영향 없음
import React, { useState, useRef, useMemo, useEffect } from "react";
import TinyMCEpreview from "../../const/TinyMCEpreview";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import { getSafetyDocDetail, saveSafetyDoc } from "../../../api/safety";
import { useParams } from "react-router-dom";

export default function MobileSCARView({ doc, currentUser = {} }) {
  const { dns } = useParams();
  const meta = doc?.meta || {};
  const editorRef = useRef(null);

  // 로그인 정보
  const myId = Number(currentUser?.id || 0);
  const myName = currentUser?.name || "알 수 없음";

  /* ========================= 상태 ========================= */
  const [isReplying, setIsReplying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resultReply, setResultReply] = useState(meta.result_reply || "");
  const [resultStatus, setResultStatus] = useState(
    meta.result_status || "completed"
  );
  const [progress, setProgress] = useState(meta.progress || "action_needed");

  // 댓글
  const [comments, setComments] = useState(meta.comments || []);
  const [newComment, setNewComment] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);

  // ✅ 상태별 한글 매핑
  const STATUS_LABELS = {
    action_needed: "조치 필요",
    in_progress: "조치 중",
    completed: "조치 완료",
    fail: "부적합",
    urgent: "긴급",
  };

  useEffect(() => {
    if (resultStatus === "completed") setProgress("completed");
    else if (resultStatus === "in_progress") setProgress("in_progress");
    else if (resultStatus === "failed" || resultStatus === "fail")
      setProgress("fail");
  }, [resultStatus]);

  // 🔸 담당자만 회신 가능
  const canReply =
    Array.isArray(meta.reference_users) &&
    myId != null &&
    meta.reference_users.includes(myId);

  const handleSaveReply = async () => {
    const html = editorRef.current?.getContent?.({ format: "html" }) || "";
    if (!html.trim()) {
      alert("조치 결과 내용을 입력해주세요.");
      return;
    }

    try {
      setSaving(true);

      // 1. 최신 meta 불러오기
      const latestDoc = await getSafetyDocDetail(dns);
      const latestMeta = latestDoc?.meta || {};

      // 2. 진행상태 갱신
      const nextProgress =
        resultStatus === "completed"
          ? "completed"
          : resultStatus === "in_progress"
          ? "in_progress"
          : "fail";

      const payload = {
        dns,
        project: doc.project,
        title: doc.title,
        doc_type: doc.doc_type,
        status: "none",
        meta: {
          ...latestMeta,
          result_reply: html,
          result_status: resultStatus,
          progress: progress,
          reply_author: myName,
          reply_date: new Date().toISOString().slice(0, 10),
        },
      };
      await saveSafetyDoc(payload);
      alert("조치 결과가 저장되었습니다.");
      setIsReplying(false);
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert("저장 실패");
    } finally {
      setSaving(false);
    }
  };

  // 댓글 등록
  const handleAddComment = async () => {
    if (!newComment.trim()) return alert("댓글을 입력해주세요.");

    const newItem = {
      author_id: myId,
      author: myName,
      content: newComment.trim(),
      created_at: new Date().toISOString(),
    };

    try {
      setCommentSaving(true);

      const latestDoc = await getSafetyDocDetail(dns);
      const latestMeta = latestDoc?.meta || {};
      const serverComments = Array.isArray(latestMeta.comments)
        ? latestMeta.comments
        : [];

      const updatedComments = [...serverComments, newItem];

      setComments(updatedComments);
      setNewComment("");

      const payload = {
        dns,
        project: doc.project,
        title: doc.title,
        doc_type: doc.doc_type,
        status: "none",
        meta: {
          ...latestMeta,
          comments: updatedComments,
        },
      };

      await saveSafetyDoc(payload);
    } catch (err) {
      console.error(err);
      alert("댓글 저장 실패");
    } finally {
      setCommentSaving(false);
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (index) => {
    if (!window.confirm("이 댓글을 삭제하시겠습니까?")) return;
    const updated = comments.filter((_, i) => i !== index);
    setComments(updated);

    try {
      const payload = {
        dns,
        project: doc.project,
        title: doc.title,
        doc_type: doc.doc_type,
        status: "none",
        meta: {
          ...meta,
          comments: updated,
        },
      };
      await saveSafetyDoc(payload);
    } catch (err) {
      console.error(err);
      alert("삭제 실패");
    }
  };

  /* ========================= 렌더링 ========================= */
  const has = (v) => v !== undefined && v !== null && String(v).trim() !== "";
  const detailRows = useMemo(
    () => [
      { label: "안전 위반 사항", value: meta.scarProblem },
      { label: "즉시 조치 사항", value: meta.scarImmediate },
      { label: "발생 원인", value: meta.scarRootCause },
    ],
    [meta.scarProblem, meta.scarImmediate, meta.scarRootCause]
  );

  const sectionStyle = "rounded-lg border bg-white p-3 min-w-0 w-full";
  const sectionStyleObj = { width: '100%', maxWidth: '100%', boxSizing: 'border-box' };

  return (
    <div className="w-full min-w-0 overflow-x-hidden flex flex-col items-center px-1 space-y-4" style={{ maxWidth: 'calc(100vw - 0.5rem)' }}>
      {/* 조치 요청 내용 */}
      <section className={sectionStyle} style={sectionStyleObj}>
        <div className="text-sm font-semibold mb-4 bg-gray-50 -m-4 mb-4 p-3 rounded-t-lg text-gray-900">조치 요청 내용</div>
        <div className="min-w-0 w-full">
          <TinyMCEpreview
            html={meta.instructions || "<p>요청 내용을 찾을 수 없습니다.</p>"}
          />
        </div>
      </section>

      {/* 상세 내용 - 모바일: 각 필드를 세로로 쌓기 */}
      <section className={sectionStyle} style={sectionStyleObj}>
        <div className="text-sm font-semibold mb-4 text-gray-900">상세 내용</div>
        <div className="space-y-4 min-w-0 w-full">
          {detailRows
            .filter((r) => has(r.value))
            .map((r, i) => (
              <div key={i} className="flex flex-col gap-1.5 min-w-0 w-full border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                <div className="text-xs text-gray-600 font-medium">{r.label}</div>
                <div className="text-sm text-gray-900 break-words leading-relaxed">{r.value}</div>
              </div>
            ))}
        </div>
      </section>

      {/* 사진 */}
      {Array.isArray(meta.photos) && meta.photos.length > 0 && (
        <section className={sectionStyle} style={sectionStyleObj}>
          <div className="text-sm font-semibold mb-4 text-gray-900">첨부 사진</div>
          <div className="grid grid-cols-2 gap-3 w-full">
            {meta.photos.map((p, i) => (
              <div key={i} className="w-full aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                <img
                  src={p.url}
                  alt={p.name || `사진 ${i + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 결과 회신 */}
      {meta.result_reply && !isReplying && (
        <section className={sectionStyle} style={sectionStyleObj}>
          <div className="text-sm font-semibold mb-4 bg-gray-50 -m-4 mb-4 p-3 rounded-t-lg text-gray-900">조치 결과</div>
          <div className="min-w-0 w-full mb-4">
            <TinyMCEpreview html={meta.result_reply} />
          </div>

          <div className="space-y-2 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="font-medium">작성자:</span>
              <span>{meta.reply_author || "-"}</span>
              <span className="text-gray-300">|</span>
              <span className="font-medium">작성일:</span>
              <span>{meta.reply_date || "-"}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium text-gray-600">진행 상태:</span>
              <span className="font-semibold text-blue-700">
                {STATUS_LABELS[progress] || "[Error]"}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* 담당자 회신 입력 */}
      {canReply && (
        <section className={`${sectionStyle} no-print`} style={sectionStyleObj}>
          <div className="flex items-center justify-center w-full gap-2 min-h-[3rem]">
            {!isReplying && (
              <button 
                className="btn-edit flex-shrink-0 whitespace-nowrap text-xs px-3 py-1.5" 
                onClick={() => setIsReplying(true)}
                style={{ minWidth: 'fit-content' }}
              >
                {meta.result_reply
                  ? "조치 결과 수정하기"
                  : "조치 결과 작성하기"}
              </button>
            )}
          </div>

          {isReplying && (
            <div className="w-full space-y-3">
              <div className="min-w-0 w-full">
                <TinyMCEeditor initialValue={resultReply} editorRef={editorRef} />
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
                <select
                  className="border rounded px-3 py-2 text-sm w-full sm:w-auto flex-shrink-0"
                  value={resultStatus}
                  onChange={(e) => setResultStatus(e.target.value)}
                >
                  <option value="completed">조치 완료</option>
                  <option value="in_progress">진행 중</option>
                  <option value="failed">부적합</option>
                </select>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    className="btn-save flex-1 sm:flex-initial flex-shrink-0 whitespace-nowrap disabled:opacity-60"
                    onClick={handleSaveReply}
                    disabled={saving}
                  >
                    {saving ? "저장 중..." : "결과 저장"}
                  </button>
                  <button
                    className="btn-cancel flex-1 sm:flex-initial flex-shrink-0 whitespace-nowrap"
                    onClick={() => setIsReplying(false)}
                  >
                    취소
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* 댓글 입력란 */}
      <section className={`${sectionStyle} no-print`} style={sectionStyleObj}>
        <div className="text-sm font-semibold mb-4 text-gray-900">댓글</div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full mb-4">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-0 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="댓글을 입력하세요"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddComment();
              }
            }}
          />
          <button
            onClick={handleAddComment}
            disabled={commentSaving || !newComment.trim()}
            className="btn-save flex-shrink-0 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2"
          >
            {commentSaving ? "등록 중..." : "등록"}
          </button>
        </div>

        {/* 댓글 리스트 */}
        <div className="space-y-3 w-full">
          {Array.isArray(comments) && comments.length > 0 ? (
            comments
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
              .map((c, i) => (
                <div
                  key={i}
                  className="border border-gray-200 rounded-lg p-3 bg-gray-50 w-full flex justify-between items-start gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 break-words leading-relaxed mb-1.5">{c.content}</div>
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">{c.author}</span>
                      {" · "}
                      {new Date(c.created_at).toLocaleString("ko-KR", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  {Number(myId) === Number(c.author_id) && (
                    <button
                      onClick={() => handleDeleteComment(i)}
                      className="text-gray-400 hover:text-red-500 text-base px-2 py-1 flex-shrink-0 transition-colors"
                      title="삭제"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))
          ) : (
            <div className="text-center text-sm text-gray-400 py-4">
              댓글이 없습니다.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

